import styled from '@emotion/styled';
import {
  Button,
  Dialog as _Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { ChangeEvent, ClassAttributes, createElement, Fragment, MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { StateHandler, withStateHandlers } from 'recompose';

import theme from '../../../../theme';
import { SPACING_UNIT } from '../../../constant/spacing';
import mq from '../../../styleUtility/mediaquery';

interface Props extends ClassAttributes<HTMLElement> {
  isOpen: boolean;
  aspectRatio: {
    width: number;
    height: number;
  };
  displaySize: {
    width: number;
    height: number;
  };
  onSubmit: ({ width, height }: { width: number; height: number }) => void;
  onRequestClose: (e: MouseEvent<HTMLElement>) => void;
  className?: string;
}

type State = {
  width: number;
  height: number;
};

type StateUpdater = {
  onEnter: StateHandler<State>;
  onChangeWidth: StateHandler<State>;
  onChangeHeight: StateHandler<State>;
};

const EditAspectRatioDialog = withStateHandlers<State, StateUpdater, Props>(
  {
    width: 0,
    height: 0,
  },
  {
    onEnter: (_: any, { displaySize }: Props) => () => displaySize,
    onChangeWidth: ({ width, height }, { aspectRatio }) => (e: ChangeEvent<HTMLInputElement>) => ({
      width: Number(e.target.value) > 0 ? Number(e.target.value) : width,
      height: Number(e.target.value) > 0 ? (Number(e.target.value) * aspectRatio.height) / aspectRatio.width : height,
    }),
    onChangeHeight: ({ width, height }, { aspectRatio }) => (e: ChangeEvent<HTMLInputElement>) => ({
      width: Number(e.target.value) > 0 ? (Number(e.target.value) * aspectRatio.width) / aspectRatio.height : width,
      height: Number(e.target.value) > 0 ? Number(e.target.value) : height,
    }),
  },
)(
  ({
    aspectRatio,
    isOpen,
    onEnter,
    onChangeWidth,
    onChangeHeight,
    onRequestClose,
    onSubmit,
    width,
    height,
    className,
  }) => {
    const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['lg']}px)`);

    return (
      <Dialog
        open={isOpen}
        onEnter={onEnter}
        onClose={onRequestClose}
        className={className}
        fullScreen={!isPC}
        scroll="paper"
      >
        <DialogTitle disableTypography>
          <Typography variant="h6">
            <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.title" />
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.label.aspect_ratio" />
          </Typography>
          <Typography>{`${aspectRatio.width}:${aspectRatio.height}`}</Typography>
          <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.label.width">
            {(label: string) => (
              <StyledTextField
                id="width"
                label={label}
                type="number"
                value={width}
                onChange={onChangeWidth}
                margin="normal"
              />
            )}
          </FormattedMessage>
          <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.label.height">
            {(label: string) => (
              <TextField
                id="height"
                label={label}
                type="number"
                value={height}
                onChange={onChangeHeight}
                margin="normal"
              />
            )}
          </FormattedMessage>
        </DialogContent>
        <DialogActions>
          {isPC ? (
            <>
              <Button color="default" onClick={onRequestClose}>
                <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.cancel" />
              </Button>
              <Button color="primary" onClick={() => onSubmit({ width, height })} disabled={width <= 0 || height <= 0}>
                <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.submit" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" size="small" color="default" onClick={onRequestClose}>
                <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.cancel" />
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => onSubmit({ width, height })}
                disabled={width <= 0 || height <= 0}
              >
                <FormattedMessage id="creative_format_editor.edit_aspect_ratio_dialog.submit" />
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

const Dialog = styled(_Dialog)`
  .MuiDialog-paperWidthSm {
    max-width: 600px;
  }
`;

const StyledTextField = styled(TextField)({
  marginRight: SPACING_UNIT * 2,
});

const DialogContent = styled(_DialogContent)({
  [mq('sp')]: {
    flex: 'none',
    '> div': {
      width: '47%',
    },
  },
});

const DialogActions = styled(_DialogActions)({
  [mq('sp')]: {
    flexDirection: 'column-reverse',
    '> button': {
      width: '100%',
      ':not(:first-of-type)': {
        marginBottom: SPACING_UNIT,
        marginLeft: 0,
      },
    },
  },
});

export default EditAspectRatioDialog;
